import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTheme } from 'styled-components';
import i18n from 'i18next';
import { toastr } from 'react-redux-toastr';

import { Container } from 'semantic-ui-react';
import Modal from 'common/components/organisms/Modal';
import { FlexContainer } from 'common/components/atoms';
import Label from 'common/typography/Label/Label';
import Error from 'common/typography/Error/Error';
import Button from 'common/components/atoms/Button';
import Checkbox from 'common/components/atoms/Checkbox';
import {
  OverallProgressContainer,
  ProgressContainer,
  ProgressFill,
  ProgressName,
  ProgressRow,
  ProgressTrack,
  ProgressValue,
  StatusBadge
} from 'common/components/molecules/CloneProgress.styled';
import { parseErrorMessage } from 'common/helpers/errors';
import { cloneStatusTheme } from 'common/helpers/cloneStatusTheme';
import { buildCloneTask, upsertCloneTask } from 'common/helpers/cloneTaskStore';
import {
  isMachineCloneFailureStatus,
  isMachineCloneFinalStatus,
  isMachineCloneSuccessStatus,
  MachineCloneStatusEnum,
  normalizeMachineCloneStatus
} from 'common/constants/machineCloneStatus';

import * as accountsListActions from '../actions/account.actions';
import { getIndustryTypes } from '../constants/account.constants';
import {
  CancelButton,
  DemoHint,
  DemoMachinesField,
  DemoToggleRow,
  InputField,
  ModalActionRow,
  TFlexContainer
} from './AddAccount.styled';

function AddAccount(props) {
  const INDUSTRY_TYPES = getIndustryTypes(i18n.t.bind(i18n));
  const [newAccountData, setNewAccountData] = useState({
    name: '',
    industry: '',
    description: '',
    is_demo: false,
    demo_machine_ids: [],
    skip_features: false
  });
  const [demoTemplateItems, setDemoTemplateItems] = useState([]);
  const [demoTemplatesLoaded, setDemoTemplatesLoaded] = useState(false);
  const [isLoadingDemoTemplates, setIsLoadingDemoTemplates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [taskMessage, setTaskMessage] = useState('');
  const [demoSourceAccountIds, setDemoSourceAccountIds] = useState([]);
  const [isDemoCloneRunning, setIsDemoCloneRunning] = useState(false);
  const [demoCloneSummary, setDemoCloneSummary] = useState({ completed_machines: 0, total_machines: 0 });
  const [demoMachineProgress, setDemoMachineProgress] = useState({});
  const [activeDemoMachineRows, setActiveDemoMachineRows] = useState([]);
  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
    }
  }, []);

  const handleIndustryChange = (value) => {
    setNewAccountData((prevData) => {
      if (value !== 'Others') {
        return { ...prevData, industry: value, industryType: '' };
      }
      return { ...prevData, industry: value };
    });
  };

  const demoSourceOptions = useMemo(() => {
    const sourceMap = new Map();
    demoTemplateItems.forEach((group) => {
      (group.machines || []).forEach((machine) => {
        const sourceId = machine.account_id || machine.source_account_id;
        if (sourceId === undefined || sourceId === null) {
          return;
        }
        if (!sourceMap.has(sourceId)) {
          sourceMap.set(sourceId, {
            value: Number(sourceId),
            text: machine.account_name || machine.source_account_name || `${i18n.t('common.site', { defaultValue: 'Site' })} ${sourceId}`
          });
        }
      });
    });
    return Array.from(sourceMap.values());
  }, [demoTemplateItems]);

  const demoMachineOptions = useMemo(() => {
    if (!demoSourceAccountIds.length) {
      return [];
    }
    const options = [];
    demoTemplateItems.forEach((group, index) => {
      const industry = group.industry || i18n.t('accounts.addAccount.unknownIndustry', { defaultValue: 'Unknown Industry' });
      const filteredMachines = (group.machines || []).filter((machine) => {
        const sourceId = machine.account_id || machine.source_account_id;
        return demoSourceAccountIds.includes(Number(sourceId));
      });
      if (!filteredMachines.length) {
        return;
      }
      options.push({
        value: `group-${industry}-${index}`,
        text: industry,
        isGroupHeader: true
      });
      filteredMachines.forEach((machine) => {
        options.push({
          value: machine.id,
          text: machine.name
        });
      });
    });
    return options;
  }, [demoTemplateItems, demoSourceAccountIds]);

  const isMachineSelected = value => newAccountData.demo_machine_ids.includes(value);
  const isSourceSelected = value => demoSourceAccountIds.includes(value);

  const toggleDemoSourceSelection = (value, checked) => {
    if (isDemoCloneRunning) {
      return;
    }
    if (typeof value !== 'number') {
      return;
    }
    setFormError('');
    setDemoSourceAccountIds((prevIds) => {
      const nextIds = new Set(prevIds);
      if (checked) {
        nextIds.delete(value);
      } else {
        nextIds.add(value);
      }
      const nextIdList = Array.from(nextIds);
      // Only drop the machines whose source site was removed. Clearing everything
      // on any toggle silently wiped the selection when a second site was added.
      setNewAccountData((prevData) => {
        const stillSelectable = new Set();
        demoTemplateItems.forEach((group) => {
          (group.machines || []).forEach((machine) => {
            const sourceId = machine.account_id || machine.source_account_id;
            if (nextIdList.includes(Number(sourceId))) {
              stillSelectable.add(machine.id);
            }
          });
        });
        return {
          ...prevData,
          demo_machine_ids: prevData.demo_machine_ids.filter(id => stillSelectable.has(id))
        };
      });
      return nextIdList;
    });
  };

  const toggleDemoMachineSelection = (value, checked) => {
    if (isDemoCloneRunning) {
      return;
    }
    if (typeof value !== 'number') {
      return;
    }
    setFormError('');
    setNewAccountData((prevData) => {
      const selectedIds = new Set(prevData.demo_machine_ids);
      if (checked) {
        selectedIds.delete(value);
      } else {
        selectedIds.add(value);
      }
      return {
        ...prevData,
        demo_machine_ids: Array.from(selectedIds)
      };
    });
  };

  const findDemoMachineName = (machineId) => {
    for (let i = 0; i < demoTemplateItems.length; i += 1) {
      const group = demoTemplateItems[i];
      const machine = (group.machines || []).find(item => Number(item.id) === Number(machineId));
      if (machine) {
        return machine.account_name ? `${machine.name} (${machine.account_name})` : machine.name;
      }
    }
    return `${i18n.t('assetHierarchy.cloneMachines.machineLabel', { defaultValue: 'Machine' })} ${machineId}`;
  };

  const extractTaskStatus = (taskState) => {
    const status = taskState
      && (taskState.state
        || taskState.status
        || (taskState.result && (taskState.result.state || taskState.result.status))
        || (taskState.meta && (taskState.meta.state || taskState.meta.status)));
    return String(status || '').toLowerCase();
  };

  const extractTaskMachineProgress = taskState => (
    (taskState && taskState.machine_progress)
    || (taskState && taskState.result && taskState.result.machine_progress)
    || (taskState && taskState.meta && taskState.meta.machine_progress)
    || {}
  );

  const extractTaskSummary = (taskState, fallbackTotal) => {
    const completed = Number(
      (taskState && taskState.completed_machines)
      || (taskState && taskState.result && taskState.result.completed_machines)
      || (taskState && taskState.meta && taskState.meta.completed_machines)
    ) || 0;
    const total = Number(
      (taskState && taskState.total_machines)
      || (taskState && taskState.result && taskState.result.total_machines)
      || (taskState && taskState.meta && taskState.meta.total_machines)
    ) || fallbackTotal;
    return {
      completed_machines: completed,
      total_machines: total
    };
  };

  const extractFailedMachineIds = (taskState) => {
    const failedIds = (taskState && taskState.failed_machine_ids)
      || (taskState && taskState.result && taskState.result.failed_machine_ids)
      || (taskState && taskState.meta && taskState.meta.failed_machine_ids)
      || [];
    return new Set((failedIds || []).map(id => String(id)));
  };

  const buildFinalDemoMachineProgress = (prevProgress, machineIds, taskState) => {
    const machineStatusMap = extractTaskMachineProgress(taskState);
    const failedMachineIds = extractFailedMachineIds(taskState);
    const nextProgress = { ...prevProgress };

    machineIds.forEach((machineId) => {
      const progressEntry = machineStatusMap[machineId] || machineStatusMap[String(machineId)] || {};
      const status = failedMachineIds.has(String(machineId))
        ? MachineCloneStatusEnum.FAILED
        : normalizeMachineCloneStatus(
          progressEntry.status
          || (prevProgress[machineId] && prevProgress[machineId].status)
          || MachineCloneStatusEnum.PENDING
        );

      nextProgress[machineId] = {
        ...prevProgress[machineId],
        ...progressEntry,
        status
      };
    });

    return nextProgress;
  };

  const hasDemoCloneFailures = (machineProgress, machineIds, taskState) => {
    const failedMachineIds = extractFailedMachineIds(taskState);
    return failedMachineIds.size > 0
      || machineIds.some(machineId => isMachineCloneFailureStatus(machineProgress[machineId] && machineProgress[machineId].status));
  };

  const extractDemoCloneProgressTaskId = createResponse => (
    createResponse
    && (createResponse.demo_clone_progress_task_id
      || createResponse.clone_to_account_task_id
      || createResponse.machine_clone_task_id
      || createResponse.clone_task_id
      || createResponse.task_id
      || createResponse.async_task_id
      || createResponse.demo_clone_task_id)
  );

  const loadDemoTemplates = async () => {
    if (demoTemplatesLoaded || isLoadingDemoTemplates) {
      return;
    }
    setIsLoadingDemoTemplates(true);
    try {
      const res = await props.accountsListActions.getDemoTemplates();
      if (!isMountedRef.current) {
        return;
      }
      setDemoTemplateItems((res && res.items) || []);
      setDemoTemplatesLoaded(true);
    } catch (error) {
      toastr.error(parseErrorMessage(error));
    } finally {
      if (isMountedRef.current) {
        setIsLoadingDemoTemplates(false);
      }
    }
  };

  const getCurrentUserId = () => (props.currentUser && props.currentUser.id) || null;

  /**
   * Publishes the clone to the shared store, which is what the Asset Hierarchy progress
   * panel reads. Machine rows carry the same shape the panel renders, so the task looks
   * identical whichever surface started it.
   */
  const registerDemoCloneTask = ({ taskId, accountId, accountName, machineRows }) => {
    upsertCloneTask(getCurrentUserId(), buildCloneTask({
      taskId,
      targetAccountId: accountId,
      targetAccountName: accountName,
      machineRows: machineRows.map(machine => ({
        id: machine.id,
        name: machine.name,
        sourceAccountName: '',
        status: MachineCloneStatusEnum.PENDING,
        new_machine_name: ''
      })),
      status: MachineCloneStatusEnum.IN_PROGRESS
    }));
  };

  /**
   * Mirrors the outcome into the shared store. The hierarchy panel polls independently
   * once it is mounted, but if the operator never opens it this keeps the stored record
   * from sitting at "in progress" forever.
   */
  const publishDemoCloneOutcome = (taskId, status, summary, errorMessage = '') => {
    upsertCloneTask(getCurrentUserId(), {
      taskId,
      status,
      errorMessage,
      ...(summary ? { summary } : {})
    });
  };

  const pollDemoCloneTask = (taskId, machineIds) => {
    const poll = async () => {
      try {
        const taskState = await props.accountsListActions.getDemoCloneTaskStatus(taskId);
        if (!isMountedRef.current) {
          return;
        }

        const machineProgress = extractTaskMachineProgress(taskState);
        const summary = extractTaskSummary(taskState, machineIds.length);
        setDemoCloneSummary(summary);

        const status = extractTaskStatus(taskState);
        const completedMachines = Number(summary.completed_machines);
        const totalMachines = Number(summary.total_machines);
        const isTaskComplete = isMachineCloneSuccessStatus(status)
          || (Number.isFinite(completedMachines)
            && Number.isFinite(totalMachines)
            && totalMachines > 0
            && completedMachines >= totalMachines);

        if (isTaskComplete) {
          let hasFailures = false;
          setIsDemoCloneRunning(false);
          setDemoMachineProgress((prevProgress) => {
            const finalProgress = buildFinalDemoMachineProgress(prevProgress, machineIds, taskState);
            hasFailures = hasDemoCloneFailures(finalProgress, machineIds, taskState);
            return finalProgress;
          });

          if (hasFailures) {
            const partialFailureMessage = i18n.t('assetHierarchy.cloneMachines.partialFailure', {
              defaultValue: 'Machine clone completed with some failures.'
            });
            setTaskMessage('');
            setFormError(partialFailureMessage);
            publishDemoCloneOutcome(taskId, MachineCloneStatusEnum.FAILED, summary, partialFailureMessage);
          } else {
            const successMessage = i18n.t('accounts.toastr.demoCloneSuccess', {
              defaultValue: 'Demo account setup completed successfully'
            });
            setFormError('');
            setTaskMessage(successMessage);
            toastr.success(successMessage);
            publishDemoCloneOutcome(taskId, MachineCloneStatusEnum.COMPLETED, summary);
          }

          props.accountsListActions.getAccountsList(null, 1);
          return;
        }

        setDemoMachineProgress((prevProgress) => {
          const nextProgress = { ...prevProgress };
          machineIds.forEach((machineId) => {
            const progressEntry = machineProgress[machineId] || machineProgress[String(machineId)] || {};
            nextProgress[machineId] = {
              ...nextProgress[machineId],
              ...progressEntry,
              status: normalizeMachineCloneStatus(
                progressEntry.status
                || (nextProgress[machineId] && nextProgress[machineId].status)
                || MachineCloneStatusEnum.PENDING
              )
            };
          });
          return nextProgress;
        });

        if (isMachineCloneFailureStatus(status)) {
          throw new Error((taskState && taskState.status) || i18n.t('accounts.toastr.demoCloneFailure', { defaultValue: 'Demo account setup failed' }));
        }

        setTaskMessage(i18n.t('accounts.addAccount.demoClonePending', { defaultValue: 'Demo machines are being cloned. This may take a moment...' }));
        pollTimerRef.current = setTimeout(poll, 1500);
      } catch (error) {
        const message = parseErrorMessage(error);
        // Publish before the mounted guard: if the operator closed the modal while the
        // clone was failing, the stored record must still reflect the failure.
        publishDemoCloneOutcome(taskId, MachineCloneStatusEnum.FAILED, null, message);
        if (!isMountedRef.current) {
          return;
        }
        setTaskMessage('');
        setIsDemoCloneRunning(false);
        setDemoMachineProgress((prevProgress) => {
          const nextProgress = { ...prevProgress };
          machineIds.forEach((machineId) => {
            const status = normalizeMachineCloneStatus(nextProgress[machineId] && nextProgress[machineId].status);
            if (!isMachineCloneFinalStatus(status)) {
              nextProgress[machineId] = {
                ...nextProgress[machineId],
                status: MachineCloneStatusEnum.FAILED
              };
            }
          });
          return nextProgress;
        });
        setFormError(message);
      }
    };

    poll();
  };

  const handleDemoToggle = async () => {
    if (isDemoCloneRunning) {
      return;
    }
    const nextValue = !newAccountData.is_demo;
    setFormError('');
    setTaskMessage('');
    setNewAccountData(prevData => ({
      ...prevData,
      is_demo: nextValue,
      demo_machine_ids: nextValue ? prevData.demo_machine_ids : []
    }));
    if (!nextValue) {
      setDemoSourceAccountIds([]);
      setDemoMachineProgress({});
      setDemoCloneSummary({ completed_machines: 0, total_machines: 0 });
      setActiveDemoMachineRows([]);
    }
    if (nextValue) {
      await loadDemoTemplates();
    }
  };

  const getDemoSelectionText = () => {
    if (!demoSourceAccountIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.machinePlaceholder', { defaultValue: 'Select machines' });
    }
    if (!newAccountData.demo_machine_ids.length) {
      return i18n.t('assetHierarchy.cloneMachines.machinePlaceholder', { defaultValue: 'Select machines' });
    }
    return i18n.t('accounts.addAccount.demoMachinesSelected', {
      defaultValue: '{{count}} machines selected',
      count: newAccountData.demo_machine_ids.length
    });
  };

  const getDemoSourceSelectionText = () => {
    if (!demoSourceAccountIds.length) {
      return i18n.t('assetHierarchy.cloneMachines.sourceSitePlaceholder', { defaultValue: 'Select source site' });
    }
    return i18n.t('assetHierarchy.cloneMachines.sourceSiteSelected', {
      defaultValue: '{{count}} sites selected',
      count: demoSourceAccountIds.length
    });
  };

  const demoProgressRows = useMemo(() => {
    // Rows only exist once a clone task has actually started. Deriving them from the
    // selection made every machine read "Pending" before anything was running.
    const rows = activeDemoMachineRows;

    return rows.map((machine) => {
      const progress = demoMachineProgress[machine.id] || {};
      return {
        ...machine,
        status: normalizeMachineCloneStatus(progress.status || MachineCloneStatusEnum.PENDING),
        new_machine_name: progress.new_machine_name
      };
    });
  }, [activeDemoMachineRows, demoMachineProgress]);

  const demoCompletedMachines = Number(demoCloneSummary.completed_machines) || 0;
  const demoTotalMachines = Number(demoCloneSummary.total_machines) || demoProgressRows.length;
  const demoProgressPercent = demoTotalMachines > 0
    ? Math.max(0, Math.min(100, Math.round((demoCompletedMachines / demoTotalMachines) * 100)))
    : 0;

  const handleCreateAccount = async () => {
    if (isSubmitting || isDemoCloneRunning) {
      return;
    }
    setFormError('');
    setTaskMessage('');

    if (newAccountData.is_demo && newAccountData.demo_machine_ids.length === 0) {
      setFormError(i18n.t('accounts.addAccount.demoMachinesRequired', { defaultValue: 'Please select at least one demo machine.' }));
      return;
    }

    const uniqueDemoMachineIds = Array.from(new Set(newAccountData.demo_machine_ids));
    const payload = {
      name: newAccountData.name.trim(),
      industry: newAccountData.industry,
      description: newAccountData.description,
      is_demo: newAccountData.is_demo
    };

    if (newAccountData.is_demo) {
      payload.demo_machine_ids = uniqueDemoMachineIds;
      payload.skip_features = !!newAccountData.skip_features;
    }

    if (newAccountData.industry === 'Others' && newAccountData.industryType) {
      payload.industryType = newAccountData.industryType;
    }

    setIsSubmitting(true);
    try {
      const createRes = await props.accountsListActions.addAccount(payload, {
        showSuccessToast: !payload.is_demo,
        refreshList: !payload.is_demo
      });

      const demoCloneProgressTaskId = extractDemoCloneProgressTaskId(createRes);
      if (createRes && demoCloneProgressTaskId) {
        toastr.success(i18n.t('accounts.toastr.demoCloneStarted', { defaultValue: 'Account created, demo cloning started' }));
        props.accountsListActions.getAccountsList(null, 1);
        const machineRows = uniqueDemoMachineIds.map(machineId => ({
          id: machineId,
          name: findDemoMachineName(machineId)
        }));
        const initialMachineProgress = {};
        machineRows.forEach((machine) => {
          initialMachineProgress[machine.id] = { status: MachineCloneStatusEnum.PENDING };
        });

        if (pollTimerRef.current) {
          clearTimeout(pollTimerRef.current);
        }
        setIsDemoCloneRunning(true);
        setActiveDemoMachineRows(machineRows);
        setDemoMachineProgress(initialMachineProgress);
        setDemoCloneSummary({
          completed_machines: 0,
          total_machines: machineRows.length
        });
        setTaskMessage(i18n.t('accounts.addAccount.demoClonePending', { defaultValue: 'Demo machines are being cloned. This may take a moment...' }));
        // Register in the shared store so the clone survives this modal. Closing it used
        // to lose the task entirely: it was never handed to the Asset Hierarchy panel, so
        // the only way to watch a 12 hour clone was to sit on this screen.
        registerDemoCloneTask({
          taskId: demoCloneProgressTaskId,
          accountId: createRes && (createRes.id || createRes.account_id),
          accountName: payload.name,
          machineRows
        });
        pollDemoCloneTask(demoCloneProgressTaskId, machineRows.map(machine => machine.id));
        return;
      }

      if (payload.is_demo) {
        toastr.success(i18n.t('accounts.toastr.createdSuccess', { defaultValue: 'Account created successfully' }));
        props.accountsListActions.getAccountsList(null, 1);
        setTaskMessage(i18n.t('accounts.toastr.createdSuccess', { defaultValue: 'Account created successfully' }));
        return;
      }
      props.onCancel();
    } catch (error) {
      const statusCode = error && error.response && error.response.status;
      if (statusCode === 403 && payload.is_demo) {
        setFormError(i18n.t('accounts.addAccount.demoForbidden', { defaultValue: 'You do not have permission to create a demo account.' }));
      } else if (statusCode === 400) {
        setFormError(parseErrorMessage(error));
      } else {
        setFormError(parseErrorMessage(error));
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div>
      <Modal
        width="550px"
        padding="0 20px"
        close={props.onCancel}
      >
        <TFlexContainer direction="column" marginbottom="40px" margintop="40px">
          <FlexContainer direction="column" alignItems="center" justifyContent="center" marginbottom="15px">
            <Label fontWeight="400" fontSize="25px" marginBottom="2px">{i18n.t('accounts.addAccount.title', { defaultValue: 'Add Account' })}</Label>
          </FlexContainer>
          <Container>
            <InputField
              label={i18n.t('accounts.addAccount.accountNameLabel', { defaultValue: 'Account Name' })}
              type="text"
              name="name"
              maxLength={70}
              value={newAccountData.name}
              onChange={e => setNewAccountData({ ...newAccountData, name: e.target.value })}
              placeholder={i18n.t('accounts.addAccount.accountNamePlaceholder', { defaultValue: 'Enter Account Name' })}
              search
            />
            <InputField
              label={i18n.t('settings.siteProfile.fields.industry', { defaultValue: 'Industry' })}
              type="select"
              value={newAccountData.industry}
              onChange={(e, d) => handleIndustryChange(d.value)}
              options={INDUSTRY_TYPES}
              placeholder={i18n.t('accounts.addAccount.industryPlaceholder', { defaultValue: 'Select Industry Type' })}
            />
            {newAccountData.industry === 'Others' && (
              <InputField
                label={i18n.t('accounts.addAccount.industryTypeLabel', { defaultValue: 'Industry Type' })}
                type="text"
                value={newAccountData.industryType}
                onChange={e => setNewAccountData({ ...newAccountData, industryType: e.target.value })}
                placeholder={i18n.t('accounts.addAccount.industryTypePlaceholder', { defaultValue: 'Enter Industry Type' })}
                search
              />
            )}
            <InputField
              label={i18n.t('accounts.addAccount.descriptionLabel', { defaultValue: 'Description' })}
              type="textarea"
              name="description"
              value={newAccountData.description}
              onChange={e => setNewAccountData({ ...newAccountData, description: e.target.value })}
              placeholder={i18n.t('accounts.addAccount.descriptionPlaceholder', { defaultValue: 'Description' })}
              search
              rows={5}
              style={{ resize: 'vertical', minHeight: '100px' }}
            />
            <DemoToggleRow>
              <Checkbox
                value={newAccountData.is_demo}
                disabled={isDemoCloneRunning || isSubmitting}
                onChange={handleDemoToggle}
              />
              <Label
                onClick={() => {
                  if (!isDemoCloneRunning && !isSubmitting) {
                    handleDemoToggle();
                  }
                }}
                hover
                margin="0 0 0 10px"
                fontWeight="700"
              >
                {i18n.t('accounts.addAccount.markAsDemo', { defaultValue: 'Mark as Demo Account' })}
              </Label>
            </DemoToggleRow>
            {newAccountData.is_demo && (
              <>
                <DemoMachinesField
                  label={i18n.t('assetHierarchy.cloneMachines.sourceSite', { defaultValue: 'Source Site' })}
                  type="select"
                  options={demoSourceOptions}
                  marginBottom="6px"
                  multiCheckbox
                  showSearchInMenu
                  searchPlaceholder={i18n.t('assetHierarchy.cloneMachines.searchSourceSites', { defaultValue: 'Type to search sites...' })}
                  toggleSelection={toggleDemoSourceSelection}
                  isItemSelected={isSourceSelected}
                  text={getDemoSourceSelectionText()}
                  placeholder={i18n.t('assetHierarchy.cloneMachines.sourceSitePlaceholder', { defaultValue: 'Select source site' })}
                  disabled={isLoadingDemoTemplates || isDemoCloneRunning}
                />
                <DemoMachinesField
                  label={i18n.t('accounts.addAccount.demoMachinesLabel', { defaultValue: 'Select Included Assets (Segregated by Account)' })}
                  type="select"
                  options={demoMachineOptions}
                  marginBottom="6px"
                  multiCheckbox
                  showSearchInMenu
                  searchPlaceholder={i18n.t('accounts.addAccount.searchDemoMachines', { defaultValue: 'Type to search machines...' })}
                  toggleSelection={toggleDemoMachineSelection}
                  isItemSelected={isMachineSelected}
                  text={getDemoSelectionText()}
                  placeholder={i18n.t('accounts.addAccount.demoMachinesPlaceholder', { defaultValue: 'Select assets from categories' })}
                  disabled={isLoadingDemoTemplates || isDemoCloneRunning || !demoSourceAccountIds.length}
                />
                {isLoadingDemoTemplates && (
                  <DemoHint>
                    {i18n.t('accounts.addAccount.loadingDemoMachines', { defaultValue: 'Loading demo machines...' })}
                  </DemoHint>
                )}
                {!!demoProgressRows.length && (
                  <OverallProgressContainer direction="column">
                    <DemoHint>
                      {i18n.t('assetHierarchy.cloneMachines.overallProgress', {
                        defaultValue: '{{completed}} / {{total}} machines cloned',
                        completed: demoCompletedMachines,
                        total: demoTotalMachines
                      })}
                    </DemoHint>
                    <ProgressTrack>
                      <ProgressFill progress={demoProgressPercent} />
                    </ProgressTrack>
                  </OverallProgressContainer>
                )}
                {!!demoProgressRows.length && (
                  <ProgressContainer direction="column">
                    {demoProgressRows.map((machine) => {
                      const badgeTheme = cloneStatusTheme(machine.status, props.theme);
                      return (
                        <ProgressRow key={machine.id}>
                          <ProgressName>{machine.name}</ProgressName>
                          <ProgressValue>
                            <StatusBadge backgroundColor={badgeTheme.backgroundColor} color={badgeTheme.color}>
                              {machine.status.replace('_', ' ')}
                            </StatusBadge>
                            {machine.new_machine_name ? ` (${machine.new_machine_name})` : ''}
                          </ProgressValue>
                        </ProgressRow>
                      );
                    })}
                  </ProgressContainer>
                )}
              </>
            )}
            {!!taskMessage && (
              <DemoHint>
                {taskMessage}
              </DemoHint>
            )}
            {!!formError && (
              <Error>{formError}</Error>
            )}
            <ModalActionRow justifyContent="flex-end" alignItems="center">
              <CancelButton onClick={props.onCancel} secondary="#000">{i18n.t('common.cancelUppercase', { defaultValue: 'CANCEL' })}</CancelButton>
              <Button
                onClick={handleCreateAccount}
                disabled={
                  (!newAccountData.name
                    || !newAccountData.industry
                    || (newAccountData.industry === 'Others' && !newAccountData.industryType)
                    || !newAccountData.description
                    || (newAccountData.is_demo && demoSourceAccountIds.length === 0)
                    || (newAccountData.is_demo && newAccountData.demo_machine_ids.length === 0)
                    || isSubmitting
                    || isDemoCloneRunning)
                }
              >
                {i18n.t('common.addUppercase', { defaultValue: 'ADD' })}
              </Button>
            </ModalActionRow>
          </Container>
        </TFlexContainer>
      </Modal>
    </div>

  );
}

const mapStateToProps = state => ({
  // Clone tasks are stored per user, so the next person to log in on this browser does
  // not inherit someone else's clone list.
  currentUser: state.user.user
});

const mapDispatchToProps = dispatch => ({
  accountsListActions: bindActionCreators(accountsListActions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(AddAccount));
